// What goes with what: one pairing table for the PDP's "Buy them together"
// (src/components/pdp/BundleSection.tsx) and the cart drawer's suggestions
// (src/components/CartUpsell.tsx).

/** Complementary pairing whitelist (avoids audit CI-D: bar stool paired with floor lamps). */
export const COMPLEMENTARY: Record<string, string[]> = {
  sofa: ["coffee table", "ottoman", "accent chair", "rug", "side table", "throw pillow", "floor lamp"],
  sectional: ["coffee table", "ottoman", "rug", "side table", "throw pillow"],
  "coffee table": ["sofa", "sectional", "accent chair", "rug"],
  "accent chair": ["side table", "floor lamp", "throw pillow", "rug"],
  "dining table": ["dining chair", "sideboard", "chandelier", "rug"],
  "dining chair": ["dining table"],
  "bar chair": ["bar table", "bar stool", "counter stool", "pendant"],
  "bar stool": ["bar chair", "bar table", "counter stool", "pendant"],
  bed: ["dresser", "nightstand", "bench", "mirror", "headboard", "rug", "table lamp"],
  headboard: ["bed", "nightstand", "dresser"],
  dresser: ["bed", "mirror", "nightstand"],
  desk: ["office chair", "bookcase", "filing cabinet", "table lamp"],
  rug: ["sofa", "coffee table", "accent chair", "bed"],
  "side table": ["sofa", "table lamp", "accent chair"],
  "console table": ["mirror", "table lamp", "wall art"],
  nightstand: ["bed", "table lamp"],
  bookcase: ["desk", "office chair", "table lamp"],
};

const ORDER = [
  "sectional", "sofa", "coffee table", "accent chair", "dining table", "dining chair", "bar stool", "bar chair",
  "headboard", "dresser", "bed", "desk", "office chair", "rug", "ottoman", "nightstand", "side table", "end table", "console table",
  "floor lamp", "table lamp", "chandelier", "pendant", "mirror", "bench", "bookcase",
];

/** The pairing key for a product, from its type and title ("" when none applies). */
export function keyOf(title: string, productType?: string): string {
  const t = `${productType || ""} ${title}`.toLowerCase();
  const key = ORDER.find((k) => t.includes(k)) || "";
  return key === "end table" ? "side table" : key;
}

/**
 * The catalogue's product types for each pairing key (in-stock types only).
 * Ask for them with exactTypeClause (src/lib/productTypes.ts): Shopify's
 * `product_type:"Benches"` also returns Shower Benches, and a bed's three
 * add-on sections offered a teak shower bench.
 */
const KEY_TYPES: Record<string, string[]> = {
  sofa: ["Sofas", "Love Seats"],
  sectional: ["Sectionals"],
  "coffee table": ["Coffee Tables"],
  "accent chair": ["Accent Chairs"],
  "dining table": ["Dining Tables"],
  "dining chair": ["Dining Chairs"],
  "bar chair": ["Bar Chairs"],
  "bar stool": ["Bar Chairs"],
  "counter stool": ["Bar Chairs"],
  "bar table": ["Bar & Restaurant Tables"],
  pendant: ["Pendants-Hanging Lights"],
  bed: ["Standard Beds"],
  headboard: ["Headboards"],
  dresser: ["Dressers"],
  nightstand: ["Nightstands & Bedside Tables"],
  bench: ["Benches"],
  mirror: ["Mirrors"],
  rug: ["Outdoor Rugs"],
  "side table": ["End-Side Tables"],
  "throw pillow": ["Accent Throw Pillows"],
  "floor lamp": ["Floor Lamps"],
  "table lamp": ["Table Lamps"],
  ottoman: ["Ottomans"],
  sideboard: ["Sideboards"],
  chandelier: ["Chandeliers"],
  desk: ["Desks"],
  "office chair": ["Office Chairs"],
  bookcase: ["Bookcases"],
  "filing cabinet": ["Filing Cabinets"],
  "console table": ["Console Tables"],
  "wall art": ["Wall Art"],
};

/** Product types that complement this product, in the pairing table's order. */
export function complementTypes(title: string, productType?: string): string[] {
  const own = keyOf(title, productType);
  const types = (COMPLEMENTARY[own] ?? []).flatMap((k) => KEY_TYPES[k] ?? []);
  const ownTypes = new Set(KEY_TYPES[own] ?? []);
  return [...new Set(types)].filter((t) => !ownTypes.has(t) && t !== productType);
}
